import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import TechDashboard from "@/pages/TechDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import Profile from "@/pages/Profile";
import Reports from "@/pages/Reports";
import Requests from "@/pages/Requests";
import AdminUsers from "@/pages/AdminUsers";
import AdminImportCsv from "@/pages/AdminImportCsv";
import AdminSites from "@/pages/AdminSites";
import AdminCharts from "@/pages/AdminCharts";
import TimeTracking from "@/pages/TimeTracking";
import AdminTimeRecords from "@/pages/AdminTimeRecords";
import LiveShiftBoard from "@/pages/LiveShiftBoard";
import WeeklySummary from "@/pages/WeeklySummary";
import AdminAttendance from "@/pages/AdminAttendance";
import AdminAnnouncements from "@/pages/AdminAnnouncements";
import Notices from "@/pages/Notices";
import { AnnouncementsProvider } from "@/context/AnnouncementsContext";
import { AnnouncementQueue } from "@/components/AnnouncementQueue";
import Leaderboard from "@/pages/Leaderboard";
import { PwaBanners } from "@/components/PwaBanners";
import { isStandalone } from "@/lib/pwa";

function RoleRouter() {
    const { user } = useAuth();
    if (!user) return null;
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    // Installed app: technicians land straight on Time Tracking (their primary
    // action). In a normal browser tab the portal behaves exactly as before.
    return <Navigate to={isStandalone() ? "/time-tracking" : "/dashboard"} replace />;
}

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <Toaster position="top-right" richColors />
                    <PwaBanners />
                    <AnnouncementsProvider>
                    <AnnouncementQueue />
                    <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />

                        <Route
                            path="/"
                            element={
                                <ProtectedRoute>
                                    <RoleRouter />
                                </ProtectedRoute>
                            }
                        />

                        {/* Technician routes */}
                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute>
                                    <Layout>
                                        <TechDashboard />
                                    </Layout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/profile"
                            element={
                                <ProtectedRoute>
                                    <Layout>
                                        <Profile />
                                    </Layout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/reports"
                            element={
                                <ProtectedRoute>
                                    <Layout>
                                        <Reports />
                                    </Layout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/requests"
                            element={
                                <ProtectedRoute>
                                    <Layout>
                                        <Requests />
                                    </Layout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/time-tracking"
                            element={
                                <ProtectedRoute>
                                    <Layout>
                                        <TimeTracking />
                                    </Layout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/time-records"
                            element={
                                <ProtectedRoute requireRole="admin">
                                    <Layout>
                                        <AdminTimeRecords />
                                    </Layout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/live-shifts"
                            element={
                                <ProtectedRoute requireRole="admin">
                                    <Layout>
                                        <LiveShiftBoard />
                                    </Layout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/weekly-summary"
                            element={
                                <ProtectedRoute requireRole="admin">
                                    <Layout>
                                        <WeeklySummary />
                                    </Layout>
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/leaderboard"
                            element={
                                <ProtectedRoute>
                                    <Layout>
                                        <Leaderboard />
                                    </Layout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/attendance"
                            element={
                                <ProtectedRoute requireRole="admin">
                                    <Layout>
                                        <AdminAttendance />
                                    </Layout>
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/admin/announcements"
                            element={
                                <ProtectedRoute requireRole="admin">
                                    <Layout>
                                        <AdminAnnouncements />
                                    </Layout>
                                </ProtectedRoute>
                            }
                        />

                        {/* Admin routes */}
                        <Route
                            path="/admin"
                            element={
                                <ProtectedRoute requireRole="admin">
                                    <Layout>
                                        <AdminDashboard />
                                    </Layout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/users"
                            element={
                                <ProtectedRoute requireRole="admin">
                                    <Layout>
                                        <AdminUsers />
                                    </Layout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/reports"
                            element={
                                <ProtectedRoute requireRole="admin">
                                    <Layout>
                                        <Reports />
                                    </Layout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/import-csv"
                            element={
                                <ProtectedRoute requireRole="admin">
                                    <Layout>
                                        <AdminImportCsv />
                                    </Layout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/sites"
                            element={
                                <ProtectedRoute requireRole="admin">
                                    <Layout>
                                        <AdminSites />
                                    </Layout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/charts"
                            element={
                                <ProtectedRoute requireRole="admin">
                                    <Layout>
                                        <AdminCharts />
                                    </Layout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/requests"
                            element={
                                <ProtectedRoute requireRole="admin">
                                    <Layout>
                                        <Requests />
                                    </Layout>
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/notices"
                            element={
                                <ProtectedRoute>
                                    <Layout>
                                        <Notices />
                                    </Layout>
                                </ProtectedRoute>
                            }
                        />

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    </AnnouncementsProvider>
                </BrowserRouter>
            </AuthProvider>
        </div>
    );
}

export default App;
